import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Modal from "../../Modal";
import Button from "../../Button";
import FormSelect from "../../Form/FormSelect";
import FormInput from "../../Form/FormInput";
import FormDate from "../../Form/FormDate";
import {
  BRANCH_CODE_PATTERN,
  BUYER_DOC_NUMBER_RULES,
  BUYER_DOCUMENT_OPTIONS,
  BUYER_TIN_PATTERN,
  COUNTRY_CODE_OPTIONS,
  DEFAULT_BUYER_COUNTRY,
  DEFAULT_GS_CODE,
  DEFAULT_IDTKU_BRANCH,
  DEFAULT_NON_NPWP_TIN,
  DEFAULT_TAX_INVOICE_OPT,
  DEFAULT_TRX_CODE,
  DEFAULT_UNIT,
  GOOD_SERVICE_CODE_OPTIONS,
  NITKU_PATTERN,
  TAX_INVOICE_OPT_OPTIONS,
  TRX_CODE_OPTIONS,
  UNIT_CODE_OPTIONS,
} from "../../../../Constants";

const MONTH_YEAR_PATTERN = /^\d{6}$/;

const SectionTitle = ({ children }) => (
  <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-1 pt-2">
    {children}
  </h4>
);

const FakturXmlExportModal = ({ open, onClose, onSubmit, transaction }) => {
  const { t } = useTranslation();

  const partner = transaction?.partner || {};
  const business = transaction?.business || {};
  const partnerTin = partner.taxNumber || "";

  const [trxCode, setTrxCode] = useState(
    () => TRX_CODE_OPTIONS.find((o) => o.value === DEFAULT_TRX_CODE) || null,
  );
  const [taxInvoiceOpt, setTaxInvoiceOpt] = useState(
    () =>
      TAX_INVOICE_OPT_OPTIONS.find(
        (o) => o.value === DEFAULT_TAX_INVOICE_OPT,
      ) || TAX_INVOICE_OPT_OPTIONS[0],
  );
  const [unit, setUnit] = useState(
    () => UNIT_CODE_OPTIONS.find((o) => o.value === DEFAULT_UNIT) || null,
  );
  const [goodServiceCode, setGoodServiceCode] = useState(
    () =>
      GOOD_SERVICE_CODE_OPTIONS.find((o) => o.value === DEFAULT_GS_CODE) ||
      GOOD_SERVICE_CODE_OPTIONS[0],
  );
  const [taxInvoiceDate, setTaxInvoiceDate] = useState(() =>
    transaction?.createdAt ? new Date(transaction.createdAt) : null,
  );
  const [refDesc, setRefDesc] = useState(
    () => transaction?.referenceNumber || "",
  );
  const [idtkuBranch, setIdtkuBranch] = useState(DEFAULT_IDTKU_BRANCH);
  const [addInfo, setAddInfo] = useState("");
  const [customDoc, setCustomDoc] = useState("");
  const [customDocMonthYear, setCustomDocMonthYear] = useState("");
  const [facilityStamp, setFacilityStamp] = useState("");
  const [sellerTin, setSellerTin] = useState(() => business.taxNumber || "");
  const [sellerIdtku, setSellerIdtku] = useState(
    () => `${business.taxNumber || ""}${DEFAULT_IDTKU_BRANCH}`,
  );
  const [buyerTin, setBuyerTin] = useState(
    () => partnerTin || DEFAULT_NON_NPWP_TIN,
  );
  const [buyerName, setBuyerName] = useState(() => partner.name || "");
  const [buyerAddress, setBuyerAddress] = useState(() => partner.address || "");
  const [buyerEmail, setBuyerEmail] = useState(() => partner.email || "");
  const [buyerDocument, setBuyerDocument] = useState(
    () => BUYER_DOCUMENT_OPTIONS[0],
  );
  const [buyerDocumentNumber, setBuyerDocumentNumber] = useState("");
  const [buyerIdtku, setBuyerIdtku] = useState(
    () => `${partnerTin || DEFAULT_NON_NPWP_TIN}${DEFAULT_IDTKU_BRANCH}`,
  );
  const [buyerCountry, setBuyerCountry] = useState(
    () =>
      COUNTRY_CODE_OPTIONS.find((o) => o.value === DEFAULT_BUYER_COUNTRY) ||
      null,
  );
  const [stlgRate, setStlgRate] = useState("");
  const [errors, setErrors] = useState({});

  const requireDocNumber = useMemo(
    () => buyerDocument?.value !== "TIN",
    [buyerDocument],
  );

  const docRule = useMemo(
    () =>
      BUYER_DOC_NUMBER_RULES[buyerDocument?.value] ||
      BUYER_DOC_NUMBER_RULES.Other,
    [buyerDocument],
  );

  const validate = () => {
    const next = {};
    if (!taxInvoiceDate) {
      next.taxInvoiceDate = t("invoices.errors.taxInvoiceDate");
    }
    if (!trxCode?.value) {
      next.trxCode = t("invoices.errors.trxCode");
    }
    if (!sellerTin?.trim()) {
      next.sellerTin = t("invoices.errors.sellerTin");
    } else if (!BUYER_TIN_PATTERN.test(sellerTin.trim())) {
      next.sellerTin = t("invoices.errors.sellerTinFormat");
    }
    if (!sellerIdtku?.trim()) {
      next.sellerIdtku = t("invoices.errors.sellerIdtku");
    } else if (!NITKU_PATTERN.test(sellerIdtku.trim())) {
      next.sellerIdtku = t("invoices.errors.sellerIdtkuFormat");
    }
    if (!unit?.value) {
      next.unit = t("invoices.errors.unitCode");
    }
    if (!goodServiceCode?.value) {
      next.goodServiceCode = t("invoices.errors.goodServiceCode");
    }
    if (!idtkuBranch?.trim()) {
      next.idtkuBranch = t("invoices.errors.idtkuBranch");
    } else if (!BRANCH_CODE_PATTERN.test(idtkuBranch.trim())) {
      next.idtkuBranch = t("invoices.errors.idtkuBranchFormat");
    }
    if (!buyerTin?.trim()) {
      next.buyerTin = t("invoices.errors.buyerTin");
    } else if (!BUYER_TIN_PATTERN.test(buyerTin.trim())) {
      next.buyerTin = t("invoices.errors.buyerTinFormat");
    }
    if (!buyerName?.trim()) {
      next.buyerName = t("invoices.errors.buyerName");
    }
    if (!buyerCountry?.value) {
      next.buyerCountry = t("invoices.errors.buyerCountry");
    }
    if (requireDocNumber) {
      if (!buyerDocumentNumber?.trim()) {
        next.buyerDocumentNumber = t("invoices.errors.buyerDocNumber");
      } else if (
        docRule.pattern &&
        !docRule.pattern.test(buyerDocumentNumber.trim())
      ) {
        next.buyerDocumentNumber = t(docRule.errorKey);
      }
    }
    if (!buyerIdtku?.trim()) {
      next.buyerIdtku = t("invoices.errors.buyerIdtku");
    } else if (!NITKU_PATTERN.test(buyerIdtku.trim())) {
      next.buyerIdtku = t("invoices.errors.buyerIdtkuFormat");
    }
    if (
      customDocMonthYear?.trim() &&
      !MONTH_YEAR_PATTERN.test(customDocMonthYear.trim())
    ) {
      next.customDocMonthYear = t("invoices.errors.customDocMonthYear");
    }
    if (stlgRate?.toString().trim()) {
      const rate = Number(stlgRate);
      if (Number.isNaN(rate) || rate < 0 || rate > 100) {
        next.stlgRate = t("invoices.errors.stlgRate");
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      trxCode: trxCode.value,
      taxInvoiceOpt: taxInvoiceOpt.value,
      taxInvoiceDate: taxInvoiceDate
        ? dayjs(taxInvoiceDate).format("YYYY-MM-DD")
        : "",
      refDesc: refDesc.trim(),
      unit: unit.value,
      goodServiceCode: goodServiceCode.code || goodServiceCode.value,
      idtkuBranch: idtkuBranch.trim(),
      addInfo: addInfo.trim(),
      customDoc: customDoc.trim(),
      customDocMonthYear: customDocMonthYear.trim(),
      facilityStamp: facilityStamp.trim(),
      sellerTin: sellerTin.trim(),
      sellerIdtku: sellerIdtku.trim(),
      buyerTin: buyerTin.trim(),
      buyerName: buyerName.trim(),
      buyerAddress: buyerAddress.trim(),
      buyerEmail: buyerEmail.trim(),
      buyerDocument: buyerDocument.value,
      buyerDocumentNumber: requireDocNumber ? buyerDocumentNumber.trim() : "-",
      buyerIdtku: buyerIdtku.trim(),
      buyerCountry: buyerCountry.value,
      stlgRate: stlgRate?.toString().trim() ? Number(stlgRate) : 0,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("invoices.modal.title.exportXml")}
      footer={
        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit}>
            {t("invoices.detail.actions.exportXml")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          {t("invoices.modal.description.exportXml")}
        </p>

        <SectionTitle>{t("invoices.sections.faktur")}</SectionTitle>

        <FormDate
          label={t("invoices.fields.taxInvoiceDate")}
          required
          selected={taxInvoiceDate}
          onChange={setTaxInvoiceDate}
          placeholderText={t("invoices.placeholders.taxInvoiceDate")}
          error={errors.taxInvoiceDate}
          onClear={() => setTaxInvoiceDate(null)}
        />

        <FormSelect
          label={t("invoices.fields.taxInvoiceOpt")}
          required
          options={TAX_INVOICE_OPT_OPTIONS}
          value={taxInvoiceOpt}
          onChange={setTaxInvoiceOpt}
          placeholder={t("invoices.placeholders.taxInvoiceOpt")}
        />

        <FormSelect
          label={t("invoices.fields.trxCode")}
          required
          options={TRX_CODE_OPTIONS}
          value={trxCode}
          onChange={setTrxCode}
          searchable
          placeholder={t("invoices.placeholders.trxCode")}
          error={errors.trxCode}
        />

        <FormInput
          label={t("invoices.fields.refDesc")}
          value={refDesc}
          onChange={(e) => setRefDesc(e.target.value)}
          onClear={() => setRefDesc("")}
          hint={t("common.optional")}
          placeholder={t("invoices.placeholders.refDesc")}
        />

        <FormInput
          label={t("invoices.fields.addInfo")}
          value={addInfo}
          onChange={(e) => setAddInfo(e.target.value)}
          onClear={() => setAddInfo("")}
          placeholder={t("invoices.placeholders.addInfo")}
          hint={t("common.optional")}
        />

        <FormInput
          label={t("invoices.fields.customDoc")}
          value={customDoc}
          onChange={(e) => setCustomDoc(e.target.value)}
          onClear={() => setCustomDoc("")}
          hint={t("common.optional")}
          placeholder={t("invoices.placeholders.customDoc")}
        />

        <FormInput
          label={t("invoices.fields.customDocMonthYear")}
          value={customDocMonthYear}
          onChange={(e) => setCustomDocMonthYear(e.target.value)}
          onClear={() => setCustomDocMonthYear("")}
          hint={t("invoices.hints.customDocMonthYear")}
          error={errors.customDocMonthYear}
          maxLength={6}
          placeholder="122024"
        />

        <FormInput
          label={t("invoices.fields.facilityStamp")}
          value={facilityStamp}
          onChange={(e) => setFacilityStamp(e.target.value)}
          onClear={() => setFacilityStamp("")}
          hint={t("common.optional")}
          placeholder={t("invoices.placeholders.facilityStamp")}
        />

        {/* 2. Seller */}
        <SectionTitle>{t("invoices.sections.seller")}</SectionTitle>

        <FormInput
          label={t("invoices.fields.sellerTin")}
          required
          value={sellerTin}
          onChange={(e) => setSellerTin(e.target.value.replace(/\D/g, ""))}
          onClear={() => setSellerTin("")}
          error={errors.sellerTin}
          maxLength={16}
          inputMode="numeric"
          placeholder="0000000000000000"
        />

        <FormInput
          label={t("invoices.fields.idtkuBranch")}
          required
          value={idtkuBranch}
          onChange={(e) => setIdtkuBranch(e.target.value.replace(/\D/g, ""))}
          onClear={() => setIdtkuBranch("")}
          hint={t("invoices.hints.idtkuBranch")}
          error={errors.idtkuBranch}
          maxLength={6}
          inputMode="numeric"
          placeholder="000000"
        />

        <FormInput
          label={t("invoices.fields.sellerIdtku")}
          required
          value={sellerIdtku}
          onChange={(e) => setSellerIdtku(e.target.value.replace(/\D/g, ""))}
          onClear={() => setSellerIdtku("")}
          hint={t("invoices.hints.sellerIdtku")}
          error={errors.sellerIdtku}
          maxLength={22}
          inputMode="numeric"
          placeholder={t("invoices.placeholders.buyerIdtku")}
        />

        {/* 3. Buyer */}
        <SectionTitle>{t("invoices.sections.buyer")}</SectionTitle>

        <FormInput
          label={t("invoices.fields.buyerTin")}
          required
          value={buyerTin}
          onChange={(e) => setBuyerTin(e.target.value.replace(/\D/g, ""))}
          onClear={() => setBuyerTin("")}
          hint={t("invoices.hints.buyerTin")}
          error={errors.buyerTin}
          maxLength={16}
          inputMode="numeric"
          placeholder={DEFAULT_NON_NPWP_TIN}
        />

        <FormSelect
          label={t("invoices.fields.buyerDoc")}
          options={BUYER_DOCUMENT_OPTIONS}
          placeholder={t("invoices.placeholders.buyerDoc")}
          value={buyerDocument}
          onChange={setBuyerDocument}
        />

        <FormSelect
          label={t("invoices.fields.buyerCountry")}
          required
          options={COUNTRY_CODE_OPTIONS}
          value={buyerCountry}
          onChange={setBuyerCountry}
          searchable
          maxRenderedOptions={50}
          placeholder={t("invoices.placeholders.buyerCountry")}
          error={errors.buyerCountry}
        />

        {requireDocNumber && (
          <FormInput
            label={t("invoices.fields.buyerDocNumber")}
            required
            value={buyerDocumentNumber}
            onChange={(e) =>
              setBuyerDocumentNumber(
                docRule.numeric
                  ? e.target.value.replace(/\D/g, "")
                  : e.target.value,
              )
            }
            onClear={() => setBuyerDocumentNumber("")}
            error={errors.buyerDocumentNumber}
            maxLength={docRule.maxLength}
            inputMode={docRule.numeric ? "numeric" : "text"}
            placeholder={t("invoices.placeholders.buyerDocNumber")}
          />
        )}

        <FormInput
          label={t("invoices.fields.buyerName")}
          required
          value={buyerName}
          onChange={(e) => setBuyerName(e.target.value)}
          onClear={() => setBuyerName("")}
          error={errors.buyerName}
          placeholder={t("invoices.placeholders.buyerName")}
        />

        <FormInput
          label={t("invoices.fields.buyerAddress")}
          value={buyerAddress}
          onChange={(e) => setBuyerAddress(e.target.value)}
          onClear={() => setBuyerAddress("")}
          hint={t("common.optional")}
          placeholder={t("invoices.placeholders.buyerAddress")}
        />

        <FormInput
          label={t("invoices.fields.buyerEmail")}
          value={buyerEmail}
          onChange={(e) => setBuyerEmail(e.target.value)}
          onClear={() => setBuyerEmail("")}
          hint={t("common.optional")}
          placeholder={t("invoices.placeholders.buyerEmail")}
        />

        <FormInput
          label={t("invoices.fields.buyerIdtku")}
          required
          value={buyerIdtku}
          onChange={(e) => setBuyerIdtku(e.target.value.replace(/\D/g, ""))}
          onClear={() => setBuyerIdtku("")}
          error={errors.buyerIdtku}
          maxLength={22}
          inputMode="numeric"
          placeholder={t("invoices.placeholders.buyerIdtku")}
        />

        {/* 4. Goods / Services */}
        <SectionTitle>{t("invoices.sections.goodService")}</SectionTitle>

        <FormSelect
          label={t("invoices.fields.goodServiceCode")}
          required
          options={GOOD_SERVICE_CODE_OPTIONS}
          value={goodServiceCode}
          onChange={setGoodServiceCode}
          searchable
          maxRenderedOptions={50}
          placeholder={t("invoices.placeholders.goodServiceCode")}
          error={errors.goodServiceCode}
        />

        <FormSelect
          label={t("invoices.fields.unitCode")}
          required
          options={UNIT_CODE_OPTIONS}
          value={unit}
          onChange={setUnit}
          searchable
          placeholder={t("invoices.placeholders.unitCode")}
          error={errors.unit}
        />

        <FormInput
          label={t("invoices.fields.stlgRate")}
          value={stlgRate}
          onChange={(e) => setStlgRate(e.target.value)}
          onClear={() => setStlgRate("")}
          error={errors.stlgRate}
          placeholder="0"
        />
      </div>
    </Modal>
  );
};

export default FakturXmlExportModal;
